// noinspection FunctionNamingConventionJS

import get from 'lodash/get.js';
import has from 'lodash/has.js';

class RecursiveSearch {
    constructor() {
        this.result = null;
        this.found = false;
        this.maxLevel = null;
    }

    /**
     * Recursively performs a specified action on each item in a collection, including nested items.
     *
     * @param {array} targets - The collection of items to process.
     * @param {string} container - The property name within each item that contains nested items.
     * @param {function} action - A function to execute on each item. The function receives the item and its index as arguments.
     */
    do(targets, container, action) {
        if (targets) {
            targets.forEach((target, index) => {
                action(target, index);

                if (has(target, container)) {
                    this.do(get(target, container), container, action);
                }
            });
        }
    }

    /**
     * Recursively calculates the maximum depth level of a nested structure within a collection of items.
     *
     * @param {array} targets - The collection of items to evaluate.
     * @param {string} container - The property name within each item that contains nested items. Defaults to 'items'.
     * @param {number} currentLevel - The current level of depth in the recursive process. Defaults to 0 and increments on each recursion.
     * @returns {number} - The maximum depth level found within the nested structure.
     */
    depthLevel(targets, container = 'items', currentLevel = 0) {
        if (this.maxLevel === null) {
            this.maxLevel = 0;
        }

        this.maxLevel = currentLevel >= this.maxLevel ? currentLevel : this.maxLevel;

        targets.forEach(target => {
            if (has(target, container)) {
                this.depthLevel(get(target, container), container, currentLevel + 1);
            }
        });

        return this.maxLevel;
    }

    /**
     * Searches for an item or a specific field of an item within a collection of items based on a matching key or condition.
     * It also recursively searches nested items in a specified container property.
     *
     * @param {array} targets - The collection of items to search through.
     * @param {object} params - Parameters for the search process.
     * @param {string|function} params.targetKey - The key of the target items to compare against the subjectKey for searching, or a function that takes an item and returns a boolean indicating a match.
     * @param {any|function} params.subjectKey - The value or function used for comparison with the targetKey value to find the desired item. If a function, it should take an item as an argument and return a boolean.
     * @param {string|null} params.field - The specific field of the item to return if found. If null, the entire item is returned.
     * @param {string} params.container - The property of the target items to search for nested items. Defaults to 'items'.
     * @returns {any} - The found field value or item, or undefined if not found.
     */
    get(targets, { targetKey = 'id', subjectKey, field, container = 'items' }) {
        if (!this.found) {
            targets.forEach(target => {
                if (!this.found) {
                    if (
                        (typeof subjectKey === 'function' && subjectKey(target)) ||
                        (typeof subjectKey !== 'function' && get(target, targetKey) === subjectKey)
                    ) {
                        this.found = true;

                        this.result = field ? get(target, field) : target;
                    } else if (has(target, container) && !this.found) {
                        this.result = this.get(get(target, container), {
                            targetKey,
                            subjectKey,
                            field,
                            container,
                        });
                    }
                }
            });
        }

        return this.result;
    }

    /**
     * Recursively inserts an item into a collection of items based on a matching key.
     * It also searches for the correct insertion point within nested items in a specified container property.
     *
     * @param {array} targets - Collection of items into which the subject is to be inserted.
     * @param {object} subject - The item to be inserted into the collection.
     * @param {object} params - Parameters for the insertion process.
     * @param {string} params.targetKey - The key of the target items to compare against the subjectKey for insertion. Defaults to 'id'.
     * @param {string} params.subjectKey - The key in the subject item used for comparison with targetKey to determine the correct insertion point.
     * @param {string} params.container - The property of the target items to search for nested items and to insert the subject into. Defaults to 'items'.
     */
    insert(targets, subject, { targetKey = 'id', subjectKey, container = 'items' }) {
        if (this.found) {
            return null;
        }

        targets.forEach(target => {
            if (get(target, targetKey) === get(subject, subjectKey)) {
                this.found = true;
                get(target, container).splice(subject.order, 0, subject);
            } else if (has(target, container) && !this.found) {
                this.result = this.insert(get(target, container), subject, {
                    targetKey,
                    subjectKey,
                    container,
                });
            }
        });

        return this.result;
    }

    /**
     * Updates a collection of items by applying a specified action to each item.
     * It recursively searches for nested items within a specified container property.
     *
     * @param {array} targets - Collection of items to be updated.
     * @param {object} params - Parameters for the update process.
     * @param {string} params.container - The property of the item to search for nested items. Defaults to 'items'.
     * @param {string} params.parentId - The parent ID used in the action function.
     * @param {function} params.action - The action function to perform on each item. It receives the item, parentId, and index as arguments.
     */
    update(targets, { container = 'items', parentId, action }) {
        if (targets) {
            targets.forEach((target, index) => {
                action(target, parentId, index);

                if (has(target, container)) {
                    this.update(get(target, container), {
                        container,
                        parentId: target.uuid,
                        action,
                    });
                }
            });
        }
    }

    /**
     * Recursively deletes an item from a collection of items based on a matching key.
     * It also searches and deletes the item from nested items in a specified container property.
     *
     * @param {array} targets - Collection of items from which the item is to be deleted.
     * @param {object} params - Parameters for the deletion process.
     * @param {string} params.targetKey - The key of the target items to compare against the subjectKey for deletion. Defaults to 'id'.
     * @param {string} params.subjectKey - The key in the item being deleted used for comparison with targetKey.
     * @param {string} params.container - The property of the target items to search for nested items and to delete the item from. Defaults to 'items'.
     */
    delete(targets, { targetKey = 'id', subjectKey, container = 'items' }) {
        targets.forEach((target, index) => {
            if (get(target, targetKey) === subjectKey) {
                targets.splice(index, 1);
            }

            if (has(target, container)) {
                this.delete(get(target, container), {
                    targetKey,
                    subjectKey,
                    container,
                });
            }
        });
    }
}

export default RecursiveSearch;
